import { SceneContainer } from "GameObjects/StoryGame/SceneContainer";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Sine, TimelineLite, TimelineMax } from "gsap";
import * as HLF from "html-living-framework";
import { Mix } from "Utils/Reflection";
import * as Topics from "Data/Topics";

// TODO: use mixins to avoid repeating implementations between this and SceneSprite
export abstract class SceneDraggable extends HLF.Input.Draggable {

    protected sprite = this.visuals.addChild(new PIXI.Sprite());
    protected _subscriptions: any[] = [];

    protected _hintTimeline?: TimelineMax;
    protected abstract cancelSelectionWhenAnotherDragStarts: boolean;

    constructor(texture?: string | PIXI.Texture, anchor?: PIXI.IPoint) {
        super();
        this.setLayoutTexture(texture || PIXI.Texture.EMPTY, anchor ? anchor.x : 0, anchor ? anchor.y : 0);
        this._subscriptions.push(
            PubSub.subscribe(HLF.DRAGGABLE_SELECTED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.DRAG_BEGIN, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.SHOW_HINT, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.HIDE_HINT, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.HIDE_ALL_HINTS, this.onMessageReceived.bind(this)),
        );
        this._outlineFilter.padding = 32;
    }

    public destroy(pDestroyOptions?: any) {
        super.destroy(pDestroyOptions);
        this._subscriptions.forEach(PubSub.unsubscribe);
        this._hintTimeline?.kill();
    }

    public setLayoutTexture(texture: string | PIXI.Texture, anchorX: number, anchorY: number) {
        if(typeof texture === 'string') {
            texture = texture.replace(/\.png$/, '');
            this.name = texture;
        }
        this.sprite.texture = (texture instanceof PIXI.Texture ? texture :
            texture ? HLF.PixiUtils.getTexture(texture) : PIXI.Texture.EMPTY) || PIXI.Texture.EMPTY;
        this.sprite.anchor.x = anchorX;
        this.sprite.anchor.y = anchorY;
        this.hitArea = this.sprite.hitArea;
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case HLF.DRAGGABLE_SELECTED:
            case HLF.DRAG_BEGIN:
                // cancel selection when another drag starts
                if (pData !== this && this.cancelSelectionWhenAnotherDragStarts) {
                    this._visuals.position.set(0);
                    this.deselect();
                }
                break;
            case Topics.SHOW_HINT:
                if (pData.target === this) {
                    if (pData.type === "pulse") {
                        this.showHint(pData);
                    }
                }
                break;
            case Topics.HIDE_HINT:
                if (pData === this) {
                    this._hintTimeline?.kill();
                }
                break;
            case Topics.HIDE_ALL_HINTS:
                this._hintTimeline?.kill();
                break;
        }
    }

    /**
     * onPointerOver
     */
    protected onPointerOver(pEvent: PIXI.InteractionEvent): void {
        super.onPointerOver(pEvent);
        this._hintTimeline?.pause();
    }

    /**
     * onPointerDown
     */
    protected onPointerDown(pEvent: PIXI.InteractionEvent): void {
        super.onPointerDown(pEvent);
        this._hintTimeline?.pause();
    }

    /**
     * onPointerUp
     */
    protected onPointerUp(pEvent: PIXI.InteractionEvent): void {
        super.onPointerUp(pEvent);
        if (!this.isSelected) {
            if (this._hintTimeline?.paused()) {
                this._hintTimeline?.restart();
            }
        }
    }

    /**
     * onPointerUpOutside
     */
    protected onPointerUpOutside(pEvent: PIXI.InteractionEvent): void {
        super.onPointerUpOutside(pEvent);
        if (!this.isSelected) {
            if (this._hintTimeline?.paused()) {
                this._hintTimeline?.restart();
            }
        }
    }

    /**
     * onPointerOut
     */
    protected onPointerOut(pEvent: PIXI.InteractionEvent): void {
        super.onPointerOut(pEvent);
        if (!this.isSelected) {
            if (this._hintTimeline?.paused()) {
                this._hintTimeline?.restart();
            }
        }
    }

    protected showHint(pData?: {delay?: number}) {
        if (this._hintTimeline !== undefined) {
            this._hintTimeline.kill();
        }
        else {
            // console.log('show hint', this.name);
            this._hintTimeline = new TimelineMax();
            this._hintTimeline.to(this._visuals, 0.5, { pixi: { scale: 1.1 }, ease: Sine.easeIn });
            this._hintTimeline.yoyo(true);
            this._hintTimeline.repeat(-1);
            this._hintTimeline.delay(pData?.delay ?? 0);
        }
    }
}
